import { Box, Button, Container, FormControlLabel, Grid, IconButton, lighten, Menu, Paper, Tooltip, Typography, useTheme, MenuItem, darken, useMediaQuery, Stack } from '@mui/material';
import Chip from '@mui/material/Chip';
import { ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import CardComponent from '../../components/Card';
import { ReactComponent as MaintIcon } from '../../assets/icons/main/machine-maint-icon.svg';
import { ReactComponent as VrLine } from '../../assets/icons/main/verticalLine.svg';
import CheckboxComponent from '../../components/Checkbox';
import { ReactComponent as PlusCircleIcon } from '../../assets/icons/main/plus-circle-icon.svg';
import SearchField from '../../common/searchField';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { palette } from '../../theme/palette';
import { get } from 'lodash';
import DataTable from '../../components/DataTable';
import { ReactComponent as FilterIcon } from '../../assets/icons/main/filter-icon.svg';
import { GridColDef } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import { maintenanceManagementActions } from '../../store/reducers/maintenance-management';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';
import { machineryManagementActions } from '../../store/reducers/machinery-management';
import CalendarDateRange from './components/CalendarDateRange';
import { ontologyMasterActions } from '../../store/reducers/ontology-master';
import dayjs from 'dayjs';
import { sentenceCase } from 'change-case';
import OntologySelector from '../../common/OntologySelector';

const Styles = {
    tableContainer: {
        container: { boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.02)', borderRadius: '18px', height: '100%', },
        paper: {
            margin: 'auto',
            boxShadow: '0px 0px 14px 0px #0000000D',
            borderRadius: '18px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        },
        viewAllBtnWrapper: { display: 'flex', justifyContent: 'end', p: 2 },
    },
    chartWrapper: {
        headerWrapper: {
            p: '22px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            boxSizing: 'border-box',
            flexWrap: 'wrap',
            rowGap: 1,
        },
    },
    dataTable: {
        '& .MuiDataGrid-virtualScroller': {
            pb: 0,
            overflow: 'auto',
            overflowY: 'hidden',
            height: '100%',
            minHeight: '280px !important',
        },
        '&.MuiDataGrid-root': {
            minHeight: '280px !important',
        },
        '.MuiDataGrid-columnHeader': {
            height: '48px !important',
        },
        '.MuiDataGrid-columnHeaders': {
            minHeight: '48px !important',
            maxHeight: '48px !important',
            lineHeight: '48px !important',
        },

        '& .MuiDataGrid-iconButtonContainer': {
            marginLeft: '2px',
            visibility: 'visible !important',
            width: 'auto !important',
        },
    },
    tableCellText: {
        fontSize: '16px',
        fontWeight: 400,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    gatewaysAssetsTableSearch: { fontSize: '16px', width: '300px', '@media (max-width: 768px)': { width: '50%' } },
    filterStyles: {
        '@media (max-width:1200px)': {
            paddingRight: '0px',
        },
        '@media (min-width:1201px) and (max-width:1375px)': {
            paddingRight: '100px',
        },
        '@media (min-width:1376px) and (max-width:1499px)': {
            paddingRight: '80px',
        },
        '@media (min-width:1498px) and (max-width:1615px)': {
            paddingRight: '60px',
        },
        '@media (min-width:1616px) and (max-width:1700px)': {
            paddingRight: '40px',
        },
        '@media (min-width:1701px) and (max-width:1900px)': {
            paddingRight: '20px',
        },
    },
};

const TableContainer = ({ children, onViewAll = () => { } }: { children: ReactNode; onViewAll: () => void }) => {
    const theme = useTheme();
    const themeMode: any = get(useOutletContext(), 'themeMode', {});
    return (
        <Container maxWidth={false} disableGutters sx={Styles.tableContainer.container}>
            <Paper
                sx={{
                    ...Styles.tableContainer.paper,
                    border: `1px solid  ${theme.palette.customBorderColor.main}`,
                    backgroundColor: themeMode.isLightBG
                        ? lighten(theme.palette.background.default, 0.9)
                        : lighten(theme.palette.background.default, 0.05),
                }}
            >
                <div style={{ top: 0 }}>{children}</div>
                <Box sx={{ ...Styles.tableContainer.viewAllBtnWrapper, borderTop: `1px solid ${theme.palette.customBorderColor.main}`, mt: '-2px'}}>
                    <Button onClick={onViewAll}>View All</Button>
                </Box>
            </Paper>
        </Container>
    );
};

const selectOptions = [
    {
        label: 'Scheduled',
        value: 'scheduled',
    },
    {
        label: 'Not Scheduled',
        value: 'not_scheduled',
    },
];

const MaintenanceManagement = ({
    isOntoManagesLoading,
    ontologyData,
    ontologies,
    maintenancePlanData,
    activityListData,
    machineList,
    isMaintenanceListLoading,
    isActivityListLoading,
    isMachineryManagementDataLoading,
    maintenanceTaslPlanCounts
}: any) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const today = new Date();
    const [selectedOntology, setSelectedOntology] = useState<any>(0)
    const [planSearchValue, setPlanSearchValue] = useState<any>('');
    const [checklistSearchValue, setChecklistSearchValue] = useState<any>('');
    const [machineSearchValue, setMachineSearchValue] = useState<any>('');
    const [dateRange, setDateRange] = useState({ start_date: new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)), end_date: new Date(today.getFullYear(), today.getMonth(), today.getDate()) });
    const [planSortModel, setPlanSortModel] = useState<any>({ field: '', sort: '' });
    const [checklistSortModel, setChecklistSortModel] = useState<any>({ field: '', sort: '' });
    const [machineSortModel, setMachineSortModel] = useState<any>({ field: '', sort: '' });
    const [maintenanceData, setMaintenanceData] = useState<any>([]);
    const [machineData, setMachineData] = useState<any>([]);
    const [scheduleFilter, setScheduledFilter] = useState<any>({ scheduled: true, notScheduled: true });
    const [anchorEl, setAnchorEl] = useState(null);
    const [scheduledStatus, setScheduledStatus] = useState<string[]>([])
    const hideVerticalLine = useMediaQuery('(max-width:1200px)');

    const dispatchMaintenanceRequest = (payload: any) => {
        const dispatchParams: any = {
            onto_manage_id: selectedOntology,
            search_item: planSearchValue,
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
            ...planSortModel,
            ...payload,
        }
        dispatch(
            maintenanceManagementActions.getMaintenaceTaskRequest(dispatchParams)
        );
    };

    const dispatchActivityRequest = (payload: any) => {
        const dispatchParams: any = {
            search_item: checklistSearchValue,
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            ...checklistSortModel,
            ...payload,
        }
        dispatch(
            maintenanceManagementActions.getActivityListRequest(dispatchParams)
        );
    };

    const dispatchMachineRequest = (payload: any) => {
        const dispatchParams: any = {
            onto_manage_id: selectedOntology,
            search_item: machineSearchValue,
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            ...machineSortModel,
            ...payload,
        }
        dispatch(
            machineryManagementActions.getMachineryManagementDataRequest(dispatchParams)
        );
    };

    useEffect(() => {
        dispatch(ontologyMasterActions.getOntologyList(''));
        dispatch(ontologyMasterActions.getOntologyMgmntList(''));
        dispatchActivityRequest({});
    }, [])

    const getOntologyData = (ontoId: any) => {
        if (ontologies.length > 0) {
            let tempOntoData: any = [];
            restartTheLoop: while (true) {
                for (const item of ontologies) {
                    if (item.onto_manage_id == ontoId) {
                        tempOntoData.unshift(item);
                        ontoId = item.parent_id;
                        continue restartTheLoop;
                    }
                }
                break;
            }

            tempOntoData.map((item: any) => {
                let getLevelName = ontologyData.filter((data: any) => {
                    return data.id === item.onto_level_number;
                })[0];

                return {
                    ...item,
                    level_name: getLevelName?.level_name || '',
                };
            });

            return tempOntoData;
        }
        return [];
    };

    useEffect(() => {
        setMaintenanceData([]);
        if (maintenancePlanData.length > 0 && ontologies.length) {
            setMaintenanceData(
                maintenancePlanData.map((item: any) => {
                    return {
                        ...item,
                        ontologyList: getOntologyData(item.onto_manage_id),
                    };
                })
            );
        } else {
            setMaintenanceData([]);
        }
    }, [maintenancePlanData, ontologies]);


    useEffect(() => {
        if (machineList.length > 0 && ontologies.length) {
            setMachineData(
                machineList.map((item: any) => {
                    return {
                        ...item,
                        ontologyList: getOntologyData(item.onto_manage_id),
                    };
                })
            );
        } else {
            setMachineData([]);
        }
    }, [machineList, ontologies]);

    const getOntoSelectedValue = (e: any) => {
        setSelectedOntology(e?.onto_manage_id);
        dispatchMaintenanceRequest({
            onto_manage_id: e?.onto_manage_id,
        });
        dispatchMachineRequest({
            onto_manage_id: e?.onto_manage_id,
        })
    };

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOptionChange = (option: string) => {
        const status = scheduledStatus.includes(option) ? scheduledStatus.filter((status: string) => status !== option) : [...scheduledStatus, option]
        setScheduledStatus(status)
        dispatchMachineRequest({
            maintenance_status: status.join(',')
        });
    }

    useEffect(() => {
        dispatchMachineRequest({});
    }, [scheduleFilter])

    const maintenancePlanColumns: GridColDef[] = [
        {
            field: 'mt_name',
            headerName: 'Maintenance Name',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => {
                return (
                    <Tooltip title={params.value} placement='bottom' arrow>
                        <Typography
                          component='span'
                          color='primary'
                          onClick={() => pageRedirect(params.row)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 400,
                            fontSize: '1rem',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {get(params, 'value', '')}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: 'onto_manage_id',
            headerName: 'Location',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => {
                const ontoList =
                    params.row.ontologyList && params.row.ontologyList.length
                        ? params.row.ontologyList.map((item: any, index: number) =>
                            params.row.ontologyList.length > 1 ? (
                                index === params.row.ontologyList.length - 1 ? (
                                    <span>{item.onto_manage_title} </span>
                                ) : (
                                    <span>{item.onto_manage_title} / </span>
                                )
                            ) : (
                                <span>{item.onto_manage_title}</span>
                            )
                        )
                        : 'N/A';

                return (
                    <Box sx={{ textWrap: 'wrap', lineHeight: '1.2', width: '200px' }}>
                        <Tooltip title={ontoList} placement='left' arrow>
                            <Typography
                                component='p'
                                sx={{
                                    textOverflow: 'ellipsis',
                                    fontSize: '16px !important',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {ontoList}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'last_task_date',
            headerName: 'Last Maintenance',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
            renderCell: (params) => {
                const lastTaskDate = (params.value && params.value !== '') ? dayjs.utc(params.value).local().format('MMM D, YYYY hh:mm A') : 'N/A';
                return (
                    <Tooltip title={lastTaskDate} arrow>
                        <Typography component='p' sx={Styles.tableCellText}>
                            {lastTaskDate}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: 'next_task_date',
            headerName: 'Next Maintenance',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                if(params.row.mt_type==='preventive'){
                  // const lastConnected = (params.value && params.value != '') ? dayjs(params.value).format('MMM D, YYYY hh:mm A') : 'NA'
                  const currentTaskDate = get(params, 'row.current_task_date', null);
                  const nextTaskDate = currentTaskDate && new Date(currentTaskDate) > new Date() ? currentTaskDate : params.value

                  return (
                    <Tooltip title={nextTaskDate} arrow>
                      <Typography component='p' sx={Styles.tableCellText}>
                        {nextTaskDate ? dayjs.utc(nextTaskDate).local().format('MMM D, YYYY hh:mm A') : 'N/A'}
                      </Typography>
                    </Tooltip>
                  );
                } else {
                    const lastConnected_cor = (params.row.start_date && params.row.start_date != '') ? dayjs.utc(params.row.start_date).local().format('MMM D, YYYY hh:mm A') : 'NA'
                    return (
                        <Tooltip title={lastConnected_cor} arrow>
                            <Typography component='p' sx={Styles.tableCellText}>
                                {lastConnected_cor}
                            </Typography>
                        </Tooltip>
                    );
                }

            },
        },
        {
            field: 'mt_status',
            headerName: 'Status',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                return (
                    <>
                        <Chip
                            label={get(params, 'value', '').replace('_', " ")}
                            className={params.value}
                            variant='outlined'
                            sx={{padding:'0px 8px 0px 8px!important', width:'130px'}}
                        />
                    </>
                );
            },
        },
        {
            field: 'assigned_to_name',
            headerName: 'Assign to',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                return (
                    <>
                        {(params.value && params.value != '') ? <Typography
                            component='span'
                            fontWeight={400}
                            fontSize={16}
                        >
                            {params.value}
                        </Typography> : <Typography
                            component='span'
                            fontWeight={400}
                            fontSize={16}
                        >
                            Not Assigned
                        </Typography>}
                    </>

                );
            },
        },
    ];

    const checklistColumn: GridColDef[] = [
        {
            field: 'checklist_name',
            headerName: 'Checklist Name',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
        },
        {
            field: 'created_date',
            headerName: 'Created/Updated Date',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => {
              const UTCToLocal = dayjs.utc(params.row.modified_date || params.value).local().format('MMM D, YYYY hh:mm A');
              return (
                <Typography
                  component='p'
                  variant='subtitle1'
                  sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                >
                  {UTCToLocal}
                </Typography>
              );
            },
        },
    ];


    const machinesColumn: GridColDef[] = [
        {
            field: 'machine_name',
            headerName: 'Machine Name',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'onto_manage_id',
            headerName: 'Location',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => {
                const ontoList =
                    params.row.ontologyList && params.row.ontologyList.length
                        ? params.row.ontologyList.map((item: any, index: number) =>
                            params.row.ontologyList.length > 1 ? (
                                index === params.row.ontologyList.length - 1 ? (
                                    <span>{item.onto_manage_title} </span>
                                ) : (
                                    <span>{item.onto_manage_title} / </span>
                                )
                            ) : (
                                <span>{item.onto_manage_title}</span>
                            )
                        )
                        : '';

                return (
                    <Box sx={{ textWrap: 'wrap', lineHeight: '1.2', width: '300px' }}>
                        <Tooltip title={ontoList} placement='left' arrow>
                            <Typography
                                component='p'
                                sx={{
                                    textOverflow: 'ellipsis',
                                    fontSize: '16px !important',
                                    fontWeight: '400',
                                    lineHeight: '1.5',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {ontoList}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'maintenance_status',
            headerName: 'Status',
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            minWidth: 200,
            renderCell: (params: any) => {
                return (
                    <>
                        <Chip
                            label={sentenceCase(params.value || 'not_scheduled')}
                            className={params.value === 'scheduled' ? 'scheduled' : 'not_scheduled'}
                            variant='outlined'
                            sx={{padding:'0px 8px 0px 8px!important',  width:'160px'}}
                        />
                    </>
                );
            },
        },
    ]


    const pageRedirect = (params: any) => {
        navigate(`/maintenance-management/history/${params.mt_id}`)
    }

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <OntologySelector
                  padding='0'
                  ontologyData={ontologyData}
                  showLayout={true}
                  hideBottomRow={true}
                  onOntoFilterChange={getOntoSelectedValue}
                  hideLine={true}
                />
              </Box>
              <Box sx={{ display: 'flex', pl: '8px' }}>
                <VrLine style={{ color: theme.palette.customBorderColor.main }} />
                <CalendarDateRange
                  defRange={'Last 7 Days'}
                  getSelectedDate={(date: any) => {
                      setDateRange(date);
                      dispatchMaintenanceRequest({
                          start_date: date.start_date,
                          end_date: date.end_date,
                      });
                  }}
                />
              </Box>
            </Box>



            <Grid mt={2} container spacing={2}>
                <Grid item xs={12} sm={12} md={6} lg={2.4}>
                    <CardComponent
                        title={'On Hold'}
                        number={maintenanceTaslPlanCounts.on_hold || 0}
                        icon={<MaintIcon style={{ color: 'white' }} />}
                        color={palette.secondary.dark}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={2.4}>
                    <CardComponent
                        title={'Overdue'}
                        number={maintenanceTaslPlanCounts.overdue || 0}
                        icon={<MaintIcon />}
                        color={palette.error.main}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={2.4}>
                    <CardComponent
                        title={'Due'}
                        number={maintenanceTaslPlanCounts.due || 0}
                        icon={<MaintIcon />}
                        color={palette.primary.main}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={2.4}>
                    <CardComponent
                        title={'In Progress'}
                        number={maintenanceTaslPlanCounts.in_progress || 0}
                        icon={<MaintIcon />}
                        color={palette.warning.main}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={2.4}>
                    <CardComponent
                        title={'Completed'}
                        number={maintenanceTaslPlanCounts.completed || 0}
                        icon={<MaintIcon />}
                        color={palette.success.dark}
                    />
                </Grid>
            </Grid>
            <Grid
                mt={2}
                container>
                <Grid mb={2} xs={12} md={12} lg={12}>
                    <TableContainer
                        onViewAll={() => {
                            navigate(`/maintenance-management/plan-list`, {
                                state: {
                                    ontoId: selectedOntology
                                }
                            });
                        }}
                    >
                        <Box sx={Styles.chartWrapper.headerWrapper}>
                            <Box>
                                <Typography component='h4' fontSize='18px' fontWeight='400' color={theme.palette.text.primary}>
                                    List of Maintenance Plans
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
                                <Box sx={{ display: 'flex', columnGap: '12px' }}>
                                    <SearchField
                                        searchPlaceholder='Search'
                                        defaultValue={planSearchValue}
                                        onSearch={(searchValue: any) => {
                                            dispatchMaintenanceRequest({
                                                search_item: searchValue,
                                            });
                                            setPlanSearchValue(searchValue);
                                            dispatch(maintenanceManagementActions.onSearchForMaintenanceItem(searchValue));
                                        }}
                                        width='300px'
                                        sx={Styles.gatewaysAssetsTableSearch}
                                    />
                                </Box>
                                <VrLine style={{ color: theme.palette.customBorderColor.main }} />
                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        navigate('/maintenance-management/plan-list/add', {
                                            state: {
                                                ontoId: selectedOntology,
                                                isFrom: 'maintenance-management'
                                            }
                                        });
                                    }}
                                >
                                    <PlusCircleIcon />
                                    Add New
                                </Button>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                height: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <DataTable
                                rows={maintenanceData}
                                columns={maintenancePlanColumns}
                                loading={isMaintenanceListLoading}
                                hideFooter
                                tableStyles={Styles.dataTable}
                                isServerPagination
                                totalRowCount={0}
                                pageNumber={0}
                                onSorting={(sortModel: { field: string; sort: string }) => {
                                    dispatchMaintenanceRequest({
                                        ...sortModel,
                                    });
                                    setPlanSortModel(sortModel);
                                }}
                                otherProps={{
                                    getRowId: (row: any) => row.id,
                                    rowHeight: 48,
                                }}
                                noRowsOverlay={() => (
                                    <Stack height='100%' alignItems='center' justifyContent='center'>
                                        {!isMaintenanceListLoading && 'Maintenance data not available'}
                                    </Stack>
                                )}
                            />
                        </Box>
                    </TableContainer>
                </Grid>


                <Grid xs={12} md={12} xl={6} sx={{ display: 'flex', justifyContent: { lg: 'flex-start', xl: 'flex-start', md: 'flex-start', sm: 'flex-start', xs: 'flex-start' } }}>
                    <Box sx={{ width: { lg: '100%', xl: '98.5%', md: '100%', sm: '100%', xs: '100%' } }}>

                        <TableContainer
                            onViewAll={() => {
                                navigate(`/maintenance-management/activity-list`);
                            }}
                        >
                            <Box sx={Styles.chartWrapper.headerWrapper}>
                                <Box>
                                    <Typography component='h4' fontSize='18px' fontWeight='400' color={theme.palette.text.primary}>
                                        List of Maintenance Activity
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
                                    <Box sx={{ display: 'flex', columnGap: '12px' }}>
                                        <SearchField
                                            searchPlaceholder='Search'
                                            defaultValue={checklistSearchValue}
                                            onSearch={(searchValue: any) => {
                                                dispatchActivityRequest({
                                                    search_item: searchValue,
                                                });
                                                setChecklistSearchValue(searchValue);
                                                dispatch(maintenanceManagementActions.onSearchForActivityItem(searchValue));
                                            }}
                                            width='250px'
                                            sx={Styles.gatewaysAssetsTableSearch}
                                        />
                                    </Box>
                                    <VrLine style={{ color: theme.palette.customBorderColor.main }} />
                                    <Button
                                        variant='contained'
                                        onClick={() => {
                                            navigate('/maintenance-management/activity-list/add')
                                        }}
                                    >

                                        <PlusCircleIcon />
                                        Add New
                                    </Button>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    height: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <DataTable
                                    rows={activityListData}
                                    columns={checklistColumn}
                                    loading={isActivityListLoading}
                                    hideFooter
                                    tableStyles={Styles.dataTable}
                                    isServerPagination
                                    totalRowCount={0}
                                    pageNumber={0}
                                    onSorting={(sortModel: { field: string; sort: string }) => {
                                        dispatchActivityRequest({
                                            ...sortModel,
                                        });
                                        setChecklistSortModel(sortModel);
                                    }}
                                    otherProps={{
                                        getRowId: (row: any) => row.checklist_id,
                                        rowHeight: 48,
                                    }}
                                    noRowsOverlay={() => (
                                        <Stack height='100%' alignItems='center' justifyContent='center'>
                                            Checklist data not available
                                        </Stack>
                                    )}
                                />
                            </Box>
                        </TableContainer>
                    </Box>
                </Grid>

                <Grid xs={12} md={12} lg={12} xl={6} sx={{ display: 'flex', marginTop: { md: '20px', xs: '20px', lg: '20px', xl: '0px' }, justifyContent: { lg: 'flex-start', xl: 'end', md: 'flex-start', sm: 'flex-start', xs: 'flex-start' } }}>
                    <Box sx={{ width: { lg: '100%', xl: '98.5%', md: '100%', sm: '100%', xs: '100%' } }}>
                        <TableContainer
                            onViewAll={() => {
                                navigate(`/maintenance-management/machine-list`, {
                                  state: {
                                    selectedOntoId: selectedOntology,
                                  }
                                });
                            }}
                        >
                            <Box sx={Styles.chartWrapper.headerWrapper}>
                                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '12px' }}>
                                    <Typography component='h4' fontSize='18px' fontWeight='400'
                                        color={theme.palette.text.primary} sx={{ textTransform: 'capitalize' }}
                                    >
                                        Machine maintenance status
                                    </Typography>
                                    <IconButton onClick={handleClick}>
                                        <FilterIcon style={{ color: theme.palette.custom.light }} />
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        sx={{
                                            top: '40px',
                                            left:'-10px',
                                            '.MuiMenu-paper': {
                                                backgroundColor: palette.primary.contrastText,
                                                boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.05)',
                                                borderRadius: '8px 8px 8px 8px',
                                                // borderTop: 'none',
                                                // padding: '10px 0'
                                            },
                                        }}
                                    >
                                        {selectOptions.map((option:any,index:number) => (
                                            <MenuItem key={'KEYMM_'+index}>
                                                <FormControlLabel
                                                    key={'KEYFC_'+index}
                                                    control=
                                                    {<CheckboxComponent checked={scheduledStatus.includes(option.value)} handleCheckboxChange={() => handleOptionChange(option.value)} />}
                                                    label={<Typography variant='subtitle1'>{option.label}</Typography>}
                                                    sx={{ margin: 0 }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Menu>

                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
                                    <Box sx={{ display: 'flex', columnGap: '12px', justifyContent: 'flex-end' }}>
                                        <SearchField
                                            searchPlaceholder='Search'
                                            defaultValue={machineSearchValue}
                                            onSearch={(searchValue: string) => {
                                                dispatchMachineRequest({
                                                    search_item: searchValue,
                                                });
                                                setMachineSearchValue(searchValue);
                                                dispatch(machineryManagementActions.onSearchForItem(searchValue));
                                            }}
                                            width='250px'
                                            sx={{ ...Styles.gatewaysAssetsTableSearch, width: '98%' }}
                                        />
                                    </Box>
                                    <VrLine style={{ color: theme.palette.customBorderColor.main }} />
                                    <Button
                                        variant='contained'
                                        sx={{ width: '228px' }}
                                        onClick={() => {
                                            navigate('/maintenance-management/plan-list/add',
                                            );
                                        }}
                                    >
                                        <PlusCircleIcon />
                                        Add Maintenance
                                    </Button>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    height: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <DataTable
                                    rows={machineData}
                                    columns={machinesColumn}
                                    loading={isMachineryManagementDataLoading}
                                    hideFooter
                                    tableStyles={Styles.dataTable}
                                    isServerPagination
                                    totalRowCount={0}
                                    pageNumber={0}
                                    onSorting={(sortModel: { field: string; sort: string }) => {
                                        dispatchMachineRequest({
                                            ...sortModel,
                                        });
                                        setMachineSortModel(sortModel);
                                    }}
                                    otherProps={{
                                        getRowId: (row: any) => row.machine_id,
                                        rowHeight: 48,
                                    }}
                                    noRowsOverlay={() => (
                                        <Stack height='100%' alignItems='center' justifyContent='center'>
                                            Machine data not available
                                        </Stack>
                                    )}
                                />
                            </Box>
                        </TableContainer>
                    </Box>
                </Grid>
            </Grid >
        </>
    )
}

const mapStateToProps = (state: any) => ({
    ontoManages: state.ontologyMaster.OntoManages,
    isOntoManagesLoading: state.ontologyMaster.isOntoManagesLoading,
    ontologyData: state.ontologyMaster.ontologyData,
    ontologies: state.ontologyMaster.ontologyMgmntData,
    maintenancePlanData: state.maintenanceManagement.maintenanceTaskPlanData.map(( mt:any, idx: number ) => ({ ...mt, id: `${mt.mt_id}-${idx}` })),
    maintenanceTaslPlanCounts: state.maintenanceManagement.maintenanceTaslPlanCounts,
    activityListData: state.maintenanceManagement.activityListData,
    machineList: state.machineryManagement.machineryManagementData,
    isMachineryManagementDataLoading: state.machineryManagement.isMachineryManagementDataLoading,
    isMaintenanceListLoading: state.maintenanceManagement.isMaintenanceTaskListLoading,
    isActivityListLoading: state.maintenanceManagement.isActivityListLoading,
});

export default connect(mapStateToProps)(MaintenanceManagement);